import React from 'react';
import { Border, TextInputProps, TextInput } from './textInput';
import { isStrictlyNumeric } from '../../util/stringutils';
import { isNullOrUndefined } from '../../util/isNullOrUndefined';

interface NumberInputProps extends Omit<TextInputProps, 'onChange' | 'value'> {
  onChange: (
    value: number | undefined,
    event?: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  value: number | undefined;
  dataCy?: string;
}

interface NumberInputPropsWithAriaLabel extends NumberInputProps {
  ariaLabel: string;
  label?: never;
}

interface NumberInputPropsWithLabel extends NumberInputProps {
  label: string;
  ariaLabel?: never;
}

export type Props = (
  | NumberInputPropsWithLabel
  | NumberInputPropsWithAriaLabel
) &
  Border;

export const NumberInput: React.FC<React.PropsWithChildren<Props>> = ({
  value,
  onChange,
  min = 0,
  onKeyPress,
  dataCy,
  ...props
}) => {
  const validateKey = (event: React.KeyboardEvent) =>
    event.key.length === 1 &&
    !isStrictlyNumeric(event.key) &&
    event.preventDefault();

  const onChangeHandler = (
    val: string | undefined,
    event?: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const parsedValue = !isNullOrUndefined(val) && val !== '' && parseInt(val);
    const parsedMinValue = typeof min === 'number' ? min : parseInt(min);

    if (typeof parsedValue === 'number' && parsedValue >= parsedMinValue) {
      onChange(parsedValue, event);
    } else {
      onChange(undefined, event);
    }
  };

  return (
    <TextInput
      min={min}
      onKeyPress={(event) => {
        validateKey(event);

        if (onKeyPress) {
          onKeyPress(event);
        }
      }}
      type={'number'}
      value={!isNullOrUndefined(value) ? value.toString() : ''}
      onChange={onChangeHandler}
      data-cy={dataCy ? dataCy : 'number-input'}
      {...props}
    />
  );
};
